import { memo, type MutableRefObject, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Popper } from '@m/alchemy-ui/Popper';
import { PopperBox } from '@m/alchemy-ui/PopperBox';
import { ArrowUpIcon } from '@m/alchemy-ui/icons/ArrowUpIcon';
import { ArrowDownIcon } from '@m/alchemy-ui/icons/ArrowDownIcon';
import { type PanelTypeOption, panelTypeOptions } from '../common/panel-types';
import {
  AddElementHint,
  AddElementItem,
  AddElementItemIcon,
  AddElementMenu as AddElementMenuList,
  AddElementTriggerContent,
  SecondaryButton
} from '../CustomPage.styled';

const messages = defineMessages({
  addElement: {
    id: 'editCustomPageAddElement',
    defaultMessage: 'Add element'
  },
  addElementHint: {
    id: 'editCustomPageAddElementHint',
    defaultMessage: 'Drag & drop the element on the page.'
  }
});

interface AddElementOptionProps {
  readonly option: PanelTypeOption;
  readonly closeMenuRef: MutableRefObject<(() => void) | null>;
}

const AddElementOption = ({ option, closeMenuRef }: AddElementOptionProps) => {
  const intl = useIntl();
  const itemRef = useRef<HTMLButtonElement>(null);
  const { typename } = option;

  useEffect(() => {
    const element = itemRef.current;

    if (!element) {
      return undefined;
    }

    return draggable({
      element,
      getInitialData: () => ({ type: 'new-panel', typename }),
      // onDragStart fires after the native dragstart completes, so unmounting this source by closing the menu is safe.
      onDragStart: () => closeMenuRef.current?.()
    });
  }, [typename, closeMenuRef]);

  return (
    <AddElementItem ref={itemRef} type="button">
      <AddElementItemIcon>{option.icon}</AddElementItemIcon>
      <span>{intl.formatMessage(option.message)}</span>
    </AddElementItem>
  );
};

const AddElementMenuComponent = () => {
  const intl = useIntl();
  const closeMenuRef = useRef<(() => void) | null>(null);

  return (
    <Popper
      placement="bottom-start"
      trigger={({ isOpen }) => (
        <SecondaryButton type="button" priority="secondary">
          <AddElementTriggerContent>
            {intl.formatMessage(messages.addElement)}
            {isOpen ? <ArrowUpIcon aria-hidden="true" /> : <ArrowDownIcon aria-hidden="true" />}
          </AddElementTriggerContent>
        </SecondaryButton>
      )}
      overlay={({ close }) => {
        closeMenuRef.current = close;

        return (
          <PopperBox hasArrow maxWidth={210}>
            <AddElementMenuList>
              <AddElementHint>{intl.formatMessage(messages.addElementHint)}</AddElementHint>
              {panelTypeOptions.map((option) => (
                <AddElementOption key={option.typename} option={option} closeMenuRef={closeMenuRef} />
              ))}
            </AddElementMenuList>
          </PopperBox>
        );
      }}
    />
  );
};

export const AddElementMenu = memo(AddElementMenuComponent);
